import { ChangeDetectorRef, Directive, OnDestroy, OnInit, ViewRef, inject } from '@angular/core';
import { Meta } from '@df/core/meta/meta';
import { Multisite } from '@df/core/multisite/multisite.interface';
import { Events } from '@df/core/service/event';
import { IMultisiteItem, MultisiteService } from '@df/core/service/multisite';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subscription, merge } from 'rxjs';
import { MultisiteRedirectService } from './multisite-redirect.service';

@Directive()
export abstract class MultisiteSwitchComponentDef implements OnInit, OnDestroy {
    protected subscriptions: Subscription = new Subscription();

    protected asyncCd = true;
    /**
     * Multisite service
     */
    protected multisiteRedirectService = inject(MultisiteRedirectService);

    private meta = inject(Meta);

    protected multisiteService = inject(MultisiteService);

    protected events = inject(Events);

    /**
     * All stores
     */
    get all() {
        return this.multisiteService.all;
    }

    /**
     * All stores
     */
    get other() {
        return this.multisiteService.other;
    }

    /**
     * Current store
     */
    get current() {
        return this.multisiteService.current;
    }

    /**
     * Class constructor with deps
     */
    constructor(
        protected _ref: ChangeDetectorRef,
        private timeoutService: TimeoutService
    ) {
        this._ref.detach();
    }

    /**
     * Subscribe to metadata changes to update the links
     */
    ngOnInit() {
        this.subscriptions.add(
            merge(this.events.on(Multisite.META_ALTERNATE_UPDATE_EVENT), this.meta.subject).subscribe(() => this.detectChanges())
        );
        this.detectChanges();
    }

    protected detectChanges() {
        const cd = () => {
            if (!(<ViewRef>this._ref).destroyed) {
                this._ref.detectChanges();
            }
        };
        this.asyncCd ? this.timeoutService.async(cd) : cd();
    }

    /**
     * On destroy hook
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /**
     * If given website is current one
     */
    isCurrent(item: IMultisiteItem): boolean {
        return item.id === this.current.id;
    }

    /**
     * Changes location using multisite service
     * what keeps current path (or uses alternate) and saves website preference
     */
    goTo(siteId: number | string, $event: Event) {
        if (this.multisiteRedirectService.to(siteId)) {
            $event.preventDefault();
        }
    }

    /**
     * Retrieves url of page we will redirect to
     * it is not bulletproof
     */
    getRedirectLink(siteId: number | string): string | undefined {
        return this.multisiteRedirectService.getLink(siteId);
    }
}
